import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  ArrowLeft, Calendar, Stethoscope, IndianRupee, Phone, Mail, AlertTriangle,
  CheckCircle2, Clock, PhoneCall, MessageCircle, Pencil, Trash2, Loader2, X,
  Sparkles, Camera, MoreVertical, User as UserIcon, Cake, Copy, Plus,
  Share2, Download, Printer, CalendarDays, ImagePlus, ShieldCheck,
} from "lucide-react";
import { openWhatsAppOutcome } from "@/lib/whatsappRouting";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import DentalChart from "@/components/app/DentalChart";
import PatientPhotoDialog from "@/components/app/PatientPhotoDialog";
import AssignDoctorDialog from "@/components/AssignDoctorDialog";
import {
  getPhoto, setPhotoVariants, removePhoto as removePhotoStore, PHOTO_EVENT,
} from "@/lib/patientPhoto";
import PostOpInstructions from "@/components/app/PostOpInstructions";
import MedicalHistoryCard from "@/components/app/MedicalHistoryCard";
import PatientFinancialPanel from "@/components/app/PatientFinancialPanel";
import MedicalFlagsStrip from "@/components/app/MedicalFlagsStrip";
import SpecialConditions from "@/components/app/SpecialConditions";
import ConsentCard from "@/components/app/ConsentCard";
import WarrantyCard from "@/components/app/WarrantyCard";
import PatientFiles from "@/components/app/PatientFiles";
import PrescriptionsCard from "@/components/app/PrescriptionsCard";
import LabCasesCard from "@/components/app/LabCasesCard";
import DentalChartTab from "@/components/app/DentalChartV2";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DateDMYInput } from "@/components/DateDMYInput";

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E8EAE9] p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="overline text-ink-soft">{label}</div>
          <div className="mt-2 font-display text-2xl">{value}</div>
          {sub && <div className="text-[11px] text-ink-mid mt-1">{sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-full bg-linen grid place-items-center text-ink">
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cls = status === "completed" ? "bg-[#E8F0EB] text-[#2D6A4F]"
    : status === "no_show" ? "bg-[#FCECEC] text-[#B94A48]"
    : status === "cancelled" ? "bg-[#EDEDED] text-[#6B7280]"
    : status === "in_progress" ? "bg-[#FDF3ED] text-[#D06A42]"
    : "bg-[#FDF3ED] text-[#8A3F1E]";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full ${cls} uppercase tracking-widest`}>{status.replace("_"," ")}</span>;
}

function ageFromDob(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  if (years < 0 || years > 150) return null;
  return years;
}

// Resolve a patient's displayable age. Prefer the stored `age` (kept
// server-side in sync with DOB) so patients created with age-only still
// render correctly. Falls back to computing from `dob` for older records
// that only stored the DOB.
function resolvePatientAge(patient) {
  if (!patient) return null;
  if (typeof patient.age === "number" && patient.age >= 0) return patient.age;
  return ageFromDob(patient.dob);
}

// ---- Patient Header components (UI-only redesign) ---------------------------
const AVATAR_TONE = { bg: "#E7EEE9", fg: "#1A362B" };
const initialsOf = (name = "") =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

// Persist per-patient tags in localStorage (photo storage now lives in
// `lib/patientPhoto.js` and supports thumb/medium/original variants).
const tagsKey  = (pid) => `viyora.patient.${pid}.tags`;

function PatientAvatar({ patient, canEditPhoto }) {
  // The header renders the medium variant (320×320) — big enough to look
  // crisp at 96px but small enough (~30KB) to keep the header lightweight.
  const [photo, setPhoto] = useState(() => getPhoto(patient.id, "medium"));
  const [imgFailed, setImgFailed] = useState(false);
  const [hover, setHover] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("chooser");
  const [confirmRemove, setConfirmRemove] = useState(false);

  const openWith = (mode) => {
    if (!canEditPhoto) return;
    setDialogMode(mode);
    setDialogOpen(true);
  };

  const onSaved = async (variants) => {
    setPhotoVariants(patient.id, variants);
    setPhoto(variants.medium);
    setImgFailed(false);
    toast.success("Photo updated");
  };

  const doRemove = () => {
    removePhotoStore(patient.id);
    setPhoto(null);
    setImgFailed(false);
    setConfirmRemove(false);
    toast.success("Photo removed");
  };

  // Keep this avatar in sync when the photo changes anywhere else in the app
  // (e.g., a bulk import, or a future receptionist upload flow). This uses
  // the shared pub-sub in `lib/patientPhoto.js` so no manual refresh needed.
  useEffect(() => {
    const onChanged = (e) => {
      if (!e?.detail?.patientId || e.detail.patientId === patient.id) {
        setPhoto(getPhoto(patient.id, "medium"));
        setImgFailed(false);
      }
    };
    window.addEventListener(PHOTO_EVENT, onChanged);
    return () => window.removeEventListener(PHOTO_EVENT, onChanged);
  }, [patient.id]);

  const showImage = !!(photo && !imgFailed);

  return (
    <div className="relative shrink-0" data-testid="patient-avatar-wrap">
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={!canEditPhoto}>
          <button
            type="button"
            data-testid="patient-avatar-btn"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            className={`relative w-24 h-24 rounded-full overflow-hidden border border-[#E8EAE9] shadow-sm focus:outline-none focus:ring-2 focus:ring-pine/40 ${
              canEditPhoto ? "cursor-pointer" : "cursor-default"
            }`}
            aria-label={canEditPhoto ? "Change patient photo" : "Patient photo"}
          >
            {showImage ? (
              <img
                src={photo}
                alt={patient.name}
                className="w-full h-full object-cover"
                onError={() => setImgFailed(true)}
              />
            ) : (
              <div className="w-full h-full grid place-items-center font-display text-3xl"
                   style={{ background: AVATAR_TONE.bg, color: AVATAR_TONE.fg }}>
                {initialsOf(patient.name)}
              </div>
            )}
            {canEditPhoto && (
              <>
                <div className={`absolute inset-0 bg-black/25 transition-opacity ${hover ? "opacity-100" : "opacity-0"}`} />
                <span className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-white shadow border border-[#E8EAE9] grid place-items-center text-ink-mid"
                      data-testid="patient-avatar-camera-badge">
                  <Camera className="w-3.5 h-3.5" />
                </span>
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        {canEditPhoto && (
          <DropdownMenuContent align="start" className="w-52 bg-white border-[#E8EAE9]">
            <DropdownMenuItem onClick={() => openWith("camera")} data-testid="avatar-take-photo">
              <Camera className="w-4 h-4 mr-2 text-ink-mid" /> Take Photo
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openWith("gallery")} data-testid="avatar-upload-gallery">
              <ImagePlus className="w-4 h-4 mr-2 text-ink-mid" /> Upload from Gallery
            </DropdownMenuItem>
            {showImage && <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setConfirmRemove(true)} data-testid="avatar-remove-photo"
                                className="text-[#B94A48] focus:text-[#B94A48] focus:bg-[#FCECEC]">
                <Trash2 className="w-4 h-4 mr-2" /> Remove Photo
              </DropdownMenuItem>
            </>}
          </DropdownMenuContent>
        )}
      </DropdownMenu>

      {canEditPhoto && (
        <PatientPhotoDialog
          open={dialogOpen}
          initialMode={dialogMode}
          patientName={patient.name}
          onClose={() => setDialogOpen(false)}
          onSave={onSaved}
        />
      )}

      {/* Remove-photo confirmation */}
      <Dialog open={confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(false)}>
        <DialogContent className="bg-white max-w-sm" data-testid="remove-photo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Remove patient photo?</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-ink-mid">
            The photo will be replaced with the patient's initials. You can upload a new one anytime.
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={() => setConfirmRemove(false)}
              data-testid="remove-photo-cancel"
              className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen"
            >
              Cancel
            </button>
            <button
              onClick={doRemove}
              data-testid="remove-photo-confirm"
              className="text-sm px-4 py-2 rounded-full bg-[#B94A48] text-white hover:bg-[#a13d3b] inline-flex items-center gap-1.5"
            >
              <Trash2 className="w-4 h-4" /> Remove
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetaRow({ icon: Icon, label, children, testid }) {
  return (
    <div className="flex items-center gap-2 min-w-0" data-testid={testid}>
      {Icon && <Icon className="w-3.5 h-3.5 text-ink-mid shrink-0" />}
      {label && <span className="text-ink-soft shrink-0">{label}</span>}
      <span className="truncate">{children}</span>
    </div>
  );
}

function AllergyBadges({ allergies }) {
  const raw = (allergies || "").trim();
  if (!raw || /^(none|n\/a|-|—)$/i.test(raw)) {
    return (
      <span data-testid="patient-allergies-none"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full bg-[#E7EEE9] text-[#1A362B]">
        <ShieldCheck className="w-3.5 h-3.5" /> No known allergies
      </span>
    );
  }
  const parts = raw.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  return (
    <div className="flex items-center flex-wrap gap-1.5" data-testid="patient-allergies-list">
      <span className="text-xs text-ink-soft inline-flex items-center gap-1">
        <AlertTriangle className="w-3.5 h-3.5 text-clay" /> Allergies:
      </span>
      {parts.map((p) => (
        <span key={p}
              className="inline-flex items-center text-xs px-2.5 py-0.5 rounded-full bg-[#F6E3E5] text-[#7A2A2E]">
          {p}
        </span>
      ))}
    </div>
  );
}

function PatientTags({ patientId }) {
  const [tags, setTags] = useState(() => {
    try { return JSON.parse(localStorage.getItem(tagsKey(patientId)) || "[]"); }
    catch { return []; }
  });
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState("");

  const persist = (next) => {
    setTags(next);
    localStorage.setItem(tagsKey(patientId), JSON.stringify(next));
  };
  const add = () => {
    const t = draft.trim();
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      toast.error("Tag already added");
    } else {
      persist([...tags, t]);
    }
    setDraft(""); setAdding(false);
  };
  const remove = (t) => persist(tags.filter((x) => x !== t));

  return (
    <div className="flex items-center flex-wrap gap-1.5" data-testid="patient-tags">
      {tags.map((t) => (
        <span key={t}
              className="inline-flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full bg-linen text-ink-mid border border-[#E8EAE9]">
          {t}
          <button onClick={() => remove(t)} className="opacity-60 hover:opacity-100" aria-label={`Remove tag ${t}`}>
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      {adding ? (
        <span className="inline-flex items-center gap-1 rounded-full border border-[#E8EAE9] px-2 py-0.5 bg-white">
          <input
            autoFocus
            data-testid="patient-tag-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") add(); if (e.key === "Escape") { setDraft(""); setAdding(false); } }}
            onBlur={add}
            className="text-[11px] bg-transparent focus:outline-none w-24"
            placeholder="Tag name"
          />
        </span>
      ) : (
        <button
          data-testid="patient-add-tag"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1 text-[11px] px-2.5 py-0.5 rounded-full border border-dashed border-[#E8EAE9] text-ink-mid hover:bg-linen">
          <Plus className="w-3 h-3" /> Add tag
        </button>
      )}
    </div>
  );
}

function HeaderActionButton({ icon: Icon, label, onClick, tone = "neutral", testid }) {
  const tones = {
    neutral: "border-[#E8EAE9] text-ink hover:bg-linen",
    pine:    "border-pine/30 text-pine hover:bg-pine/10",
    clay:    "border-clay/30 text-clay hover:bg-clay/10",
    green:   "border-[#25D366]/30 text-[#1DA850] hover:bg-[#E8F5EE]",
    sky:     "border-[#5A93B5]/30 text-[#1F4B66] hover:bg-[#E4EFF5]",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-full bg-white border transition ${tones[tone]}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {label}
    </button>
  );
}

export default function PatientProfile() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const canEdit = user?.role === "admin" || user?.role === "doctor";
  const [stats, setStats] = useState(null);
  const [patientDoc, setPatientDoc] = useState(null);
  // Photo permissions: Admin + Receptionist always; Doctor only if this
  // patient is theirs (assigned_doctor_id === user.id).
  const canEditPhoto =
    user?.role === "admin" ||
    user?.role === "receptionist" ||
    (user?.role === "doctor" &&
      (!patientDoc?.assigned_doctor_id || patientDoc?.assigned_doctor_id === user?.id));
  const [team, setTeam] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    const [s, p, t] = await Promise.all([
      api.get(`/patients/${id}/stats`),
      api.get(`/patients/${id}`),
      api.get(`/team`).catch(() => ({ data: [] })),
    ]);
    setStats(s.data);
    setPatientDoc(p.data);
    setTeam(t.data || []);
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const primaryDoctorName = useMemo(() => {
    const did = patientDoc?.assigned_doctor_id;
    if (!did) return "Unassigned";
    const t = team.find((x) => x.id === did);
    if (!t?.name) return "Unassigned";
    return /^dr\.?\s+/i.test(t.name) ? t.name : `Dr. ${t.name}`;
  }, [patientDoc, team]);

  const patientIdShort = useMemo(() => {
    if (!patientDoc) return "";
    // Show the clinic-defined Patient ID. Falls back to "—" when not set —
    // the internal UUID is never surfaced to end users.
    return patientDoc.clinic_patient_id || "—";
  }, [patientDoc]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/patients/${id}`);
      toast.success("Patient deleted");
      nav("/app/patients");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) toast.error("You don't have permission to delete patients");
      else toast.error(typeof detail === "string" ? detail : "Delete failed");
    } finally {
      setDeleting(false);
      setDeleteOpen(false);
    }
  };

  const callWithAI = async (purpose) => {
    try {
      const { data } = await api.post("/voice/outbound-call", { patient_id: id, purpose });
      toast.success(`AI call queued · ${data.call_id?.slice(0,8)}…`);
    } catch (err) {
      toast.error(apiError(err, "Call failed"));
    }
  };

  const sendWhatsApp = async () => {
    const p = stats?.patient;
    const next = stats?.next_appointment;
    // Never inject a hardcoded product name into a patient-facing message —
    // the outbound signature is the clinic's own name (falling back to a
    // neutral phrase), not the SaaS platform.
    const clinicSign = stats?.clinic?.name || "the clinic";
    const msg = next
      ? `Hi ${p.name}, reminder of your ${next.procedure} on ${new Date(next.start).toLocaleString()} at ${clinicSign}.`
      : `Hi ${p.name}, just checking in from ${clinicSign}. Would you like to book a visit?`;
    try {
      const { data } = await api.post("/reminders/send", { channel: "whatsapp", to: p.phone, message: msg });
      // Intelligent routing: opens wa.me deep link if the clinic is on
      // Business App mode; auto-toasts otherwise (email fallback when none).
      openWhatsAppOutcome(data, {
        onEmailFallback: () => toast.info("Please use the Share Records action to send records by email."),
      });
    } catch (err) {
      toast.error(apiError(err, "Send failed"));
    }
  };

  if (!stats) return <div className="text-ink-mid">Loading…</div>;
  const { patient, last_visit, next_appointment, total_visits, total_treatments, total_spent, outstanding, recent_treatments, recent_appointments } = stats;

  const age = resolvePatientAge(patient);
  const affectedTeeth = recent_treatments.filter((t) => t.tooth).map((t) => ({ tooth: t.tooth, status: t.status }));

  return (
    <div>
      <Link to="/app/patients" data-testid="back-to-patients" className="inline-flex items-center gap-2 text-sm text-ink-mid hover:text-ink mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to patients
      </Link>

      {/* Hero — premium white card (redesign) */}
      <div className="rounded-3xl bg-white border border-[#E8EAE9] shadow-sm p-6 mb-6" data-testid="patient-hero">
        <div className="flex flex-col lg:flex-row lg:items-start gap-6">
          {/* LEFT — Avatar with camera overlay + dropdown */}
          <PatientAvatar patient={patient} canEditPhoto={canEditPhoto} />

          {/* CENTER — Identity + meta + allergies + tags */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl tracking-tight truncate" data-testid="patient-name">{patient.name}</h1>
              <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-0.5 rounded-full bg-[#E7EEE9] text-[#1A362B]"
                    data-testid="patient-status">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2D6A4F]" /> Active
              </span>
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-ink-mid">
              <MetaRow icon={UserIcon} testid="meta-gender">{patient.gender || "—"}</MetaRow>
              <MetaRow icon={Phone} testid="meta-phone">{patient.phone || "—"}</MetaRow>
              <MetaRow icon={Cake} testid="meta-age">{age !== null ? `${age} Years` : "Age N/A"}</MetaRow>
              <MetaRow icon={Mail} testid="meta-email">
                {patient.email || <span className="italic text-ink-soft">No email added</span>}
              </MetaRow>
              <MetaRow label="Patient ID:" testid="meta-patient-id">
                <span className="font-mono text-ink" data-testid="meta-patient-id-value">{patientIdShort}</span>
                {patientDoc?.clinic_patient_id && (
                  <button
                    type="button"
                    data-testid="copy-patient-id"
                    onClick={() => {
                      navigator.clipboard.writeText(patientIdShort);
                      toast.success("Patient ID copied");
                    }}
                    className="ml-1 inline-flex items-center text-ink-soft hover:text-ink"
                    title="Copy Patient ID"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                )}
              </MetaRow>
              <MetaRow icon={Stethoscope} testid="meta-doctor">
                <span className="text-ink-soft">Primary Doctor:</span>
                {(() => {
                  const unassigned = primaryDoctorName === "Unassigned";
                  const isMine = user?.role === "doctor" && patientDoc?.assigned_doctor_id === user?.id;
                  const canEditDoctor =
                    user?.role === "admin"
                    || user?.role === "receptionist"
                    || (user?.role === "doctor" && unassigned);
                  if (unassigned && canEditDoctor) {
                    return (
                      <button
                        type="button"
                        data-testid="profile-assign-doctor"
                        onClick={() => setAssignOpen(true)}
                        className="ml-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-[#FDF3ED] text-[#8A3F1E] border border-[#EAC5AE] hover:bg-[#F9E4D3]"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-clay" />
                        {user?.role === "doctor" ? "Assign to Me" : "Assign Doctor"}
                      </button>
                    );
                  }
                  return (
                    <button
                      type="button"
                      disabled={!canEditDoctor}
                      onClick={() => canEditDoctor && setAssignOpen(true)}
                      data-testid="profile-change-doctor"
                      className={`ml-1 inline-flex items-center gap-1 ${
                        unassigned ? "italic text-ink-soft" : "text-ink"
                      } ${canEditDoctor ? "hover:underline cursor-pointer" : "cursor-default"}`}
                      title={canEditDoctor ? "Change treating doctor" : (isMine ? "You are the primary treating doctor" : "Read-only for your role")}
                    >
                      {primaryDoctorName}
                      {canEditDoctor && <Pencil className="w-3 h-3 opacity-40" />}
                    </button>
                  );
                })()}
              </MetaRow>
            </div>

            <div className="mt-4">
              <AllergyBadges allergies={patient.allergies} />
            </div>

            <div className="mt-3">
              <PatientTags patientId={patient.id} />
            </div>
          </div>

          {/* RIGHT — Actions */}
          <div className="flex flex-wrap items-center gap-2 shrink-0 lg:justify-end" data-testid="patient-header-actions">
            <HeaderActionButton
              icon={CalendarDays}
              label="Appointment"
              tone="neutral"
              onClick={() => nav(`/app/calendar?patient_id=${patient.id}`)}
              testid="btn-appointment"
            />
            <HeaderActionButton
              icon={Sparkles}
              label="Chairside"
              tone="clay"
              onClick={() => nav(`/app/chairside/${patient.id}`)}
              testid="btn-chairside-mode"
            />
            <HeaderActionButton
              icon={MessageCircle}
              label="WhatsApp"
              tone="green"
              onClick={sendWhatsApp}
              testid="btn-send-whatsapp"
            />
            <HeaderActionButton
              icon={PhoneCall}
              label="Call"
              tone="sky"
              onClick={() => callWithAI("reminder")}
              testid="btn-call-with-ai"
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  data-testid="patient-header-more"
                  className="w-9 h-9 rounded-full grid place-items-center border border-[#E8EAE9] bg-white text-ink-mid hover:bg-linen"
                  aria-label="More actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52 bg-white border-[#E8EAE9]">
                {canEdit && (
                  <DropdownMenuItem onClick={() => setEditOpen(true)} data-testid="btn-edit-patient">
                    <Pencil className="w-4 h-4 mr-2 text-ink-mid" /> Edit Patient
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success("Profile link copied");
                  }}
                  data-testid="btn-share-profile"
                >
                  <Share2 className="w-4 h-4 mr-2 text-ink-mid" /> Share Profile
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => toast.info("Download starting — a full PDF export runs in the background.")}
                  data-testid="btn-download-records"
                >
                  <Download className="w-4 h-4 mr-2 text-ink-mid" /> Download Records
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => window.print()}
                  data-testid="btn-print-summary"
                >
                  <Printer className="w-4 h-4 mr-2 text-ink-mid" /> Print Summary
                </DropdownMenuItem>
                {canEdit && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteOpen(true)}
                      data-testid="btn-delete-patient"
                      className="text-[#B94A48] focus:text-[#B94A48] focus:bg-[#FCECEC]"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete Patient
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-visits" label="Total visits" value={total_visits} icon={CheckCircle2} />
        <StatCard testid="stat-treatments" label="Treatments" value={total_treatments} icon={Stethoscope} />
        <StatCard testid="stat-spent" label="Total spent" value={`₹${total_spent.toLocaleString("en-IN")}`} sub={outstanding > 0 ? `₹${outstanding.toLocaleString("en-IN")} outstanding` : "All paid"} icon={IndianRupee} />
        <StatCard testid="stat-next" label="Next visit" value={next_appointment ? new Date(next_appointment.start).toLocaleDateString(undefined,{month:"short",day:"numeric"}) : "—"} sub={next_appointment ? next_appointment.procedure : "None scheduled"} icon={Calendar} />
      </div>

      {/* Clinical safety conditions */}
      {patientDoc && (
        <div className="mb-6">
          <MedicalFlagsStrip
            patientId={patient.id}
            refreshKey={(patientDoc.medical_history || []).length}
          />
          <SpecialConditions patient={patientDoc} onReload={load} />
        </div>
      )}

      <Tabs
        defaultValue={(() => {
          if (typeof window === "undefined") return "overview";
          // Honor deep links like /app/patients/<id>?tab=chart so shortcuts
          // from the sidebar / QR scan / test automations open the right
          // section directly instead of forcing an extra click.
          const t = new URLSearchParams(window.location.search).get("tab");
          return ["overview", "chart"].includes(t) ? t : "overview";
        })()}
        className="w-full"
      >
        <TabsList data-testid="patient-tabs" className="bg-linen border border-[#E8EAE9] mb-6">
          <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-pine data-[state=active]:text-linen">Overview</TabsTrigger>
          <TabsTrigger value="chart" data-testid="tab-chart" className="data-[state=active]:bg-pine data-[state=active]:text-linen">Dental chart</TabsTrigger>
        </TabsList>

        <TabsContent value="chart" className="focus-visible:outline-none">
          <DentalChartTab patient={patient} />
        </TabsContent>

        <TabsContent value="overview" className="focus-visible:outline-none">

      {/* Last visit + Dental chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="card-widget lg:col-span-1" data-testid="last-visit-card">
          <div className="overline text-ink-soft mb-3">Last visit</div>
          {last_visit ? (
            <>
              <div className="font-display text-2xl tracking-tight">{last_visit.procedure}</div>
              <div className="mt-2 flex items-center gap-2 text-sm text-ink-mid">
                <Clock className="w-4 h-4" />
                {new Date(last_visit.start).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
              </div>
              <div className="mt-4 flex items-center gap-2 text-sm">
                <StatusPill status={last_visit.status} />
                <span className="text-ink-mid">· {last_visit.duration_min} min</span>
              </div>
              {last_visit.notes && (
                <div className="mt-4 rounded-xl bg-linen p-3 text-xs text-ink/80 whitespace-pre-wrap">{last_visit.notes}</div>
              )}
            </>
          ) : (
            <div className="text-sm text-ink-soft py-6 text-center">No completed visits yet.</div>
          )}
        </div>

        <div className="card-widget lg:col-span-2">
          <div className="overline text-ink-soft mb-3">Dental chart</div>
          <DentalChart affected={affectedTeeth} />
        </div>
      </div>

      {/* Treatment timeline + appointment history */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-widget" data-testid="treatment-timeline">
          <div className="overline text-ink-soft mb-4">Treatment timeline</div>
          {recent_treatments.length === 0 && <div className="text-sm text-ink-soft">No treatments recorded.</div>}
          <div className="relative">
            {recent_treatments.length > 0 && <div className="absolute left-3 top-2 bottom-2 w-px bg-[#E8EAE9]" />}
            <div className="space-y-4">
              {recent_treatments.map((t) => (
                <div key={t.id} className="relative pl-10" data-testid={`tx-${t.id}`}>
                  <div className={`absolute left-1.5 top-1 w-4 h-4 rounded-full border-2 ${
                    t.status === "completed" ? "bg-pine border-pine" :
                    t.status === "in_progress" ? "bg-[#D06A42] border-[#D06A42]" :
                    "bg-white border-pine-soft"
                  }`} />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium">{t.procedure}</div>
                      <div className="text-xs text-ink-mid mt-0.5">
                        Tooth {t.tooth || "—"} · {t.date?.slice(0,10)}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-sm">₹{t.cost.toLocaleString("en-IN")}</div>
                      <StatusPill status={t.status} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="card-widget" data-testid="appointment-history">
          <div className="overline text-ink-soft mb-4">Appointment history</div>
          {recent_appointments.length === 0 && <div className="text-sm text-ink-soft">No appointments.</div>}
          <div className="divide-y divide-[#E8EAE9]">
            {recent_appointments.map((a) => (
              <div key={a.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{a.procedure}</div>
                  <div className="text-xs text-ink-mid">{new Date(a.start).toLocaleString(undefined,{dateStyle:"medium", timeStyle:"short"})} · {a.duration_min}m</div>
                </div>
                <StatusPill status={a.status} />
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3">
          <MedicalHistoryCard patient={patientDoc} onReload={load} />
        </div>

        <div className="lg:col-span-3">
          <PrescriptionsCard patientId={patient.id} patient={patient} />
        </div>

        <div className="lg:col-span-3" id="lab-cases-section">
          <LabCasesCard patientId={patient.id} patient={patient} />
        </div>

        <div className="lg:col-span-3">
          <PatientFiles patientId={patient.id} patientName={patient.name} />
        </div>

        <div className="lg:col-span-3">
          <PatientFinancialPanel patient={patient} />
        </div>

        <div className="lg:col-span-3">
          <ConsentCard patient={patient} />
        </div>

        <div className="lg:col-span-3">
          <WarrantyCard patient={patient} />
        </div>

        <div className="lg:col-span-3">
          <PostOpInstructions patient={patient} />
        </div>
      </div>

        </TabsContent>
      </Tabs>

      {/* Edit patient dialog */}
      {editOpen && patientDoc && (
        <EditPatientDialog
          patient={patientDoc}
          role={user?.role}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => { setPatientDoc(updated); setEditOpen(false); toast.success("Patient updated"); load(); }}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md" data-testid="delete-patient-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-ink flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" /> Delete this patient?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-ink-mid leading-relaxed">
            You are about to permanently delete <b className="text-ink">{patient.name}</b> and all their appointments, treatments, prescriptions, files and records. <b>This action cannot be undone.</b>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => setDeleteOpen(false)} data-testid="delete-patient-cancel"
                    className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen">
              Cancel
            </button>
            <button onClick={doDelete} disabled={deleting}
                    data-testid="delete-patient-confirm"
                    className="text-sm px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Yes, delete permanently
            </button>
          </div>
        </DialogContent>
      </Dialog>
      <AssignDoctorDialog
        open={assignOpen}
        patient={{
          id: patient.id,
          name: patient.name,
          assigned_doctor_id: patientDoc?.assigned_doctor_id || null,
        }}
        team={team}
        currentUser={user}
        onClose={() => setAssignOpen(false)}
        onSaved={(newDoctorId) => {
          setPatientDoc((d) => d ? { ...d, assigned_doctor_id: newDoctorId } : d);
          setAssignOpen(false);
        }}
      />
    </div>
  );
}

// ------------------ Edit patient dialog ------------------
function EditPatientDialog({ patient, role, onClose, onSaved }) {
  const [f, setF] = useState({
    name: patient.name || "",
    phone: patient.phone || "",
    email: patient.email || "",
    dob: patient.dob || "",
    age: (typeof patient.age === "number" && patient.age >= 0)
      ? String(patient.age)
      : "",
    gender: patient.gender || "",
    address: patient.address || "",
    allergies: patient.allergies || "",
    notes: patient.notes || "",
    doctor_notes: patient.doctor_notes || "",
    clinic_patient_id: patient.clinic_patient_id || "",
  });
  const [saving, setSaving] = useState(false);
  const canWriteDoctorNotes = role === "admin" || role === "doctor";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f };
      if (!canWriteDoctorNotes) delete body.doctor_notes;
      // Normalize empty ID to null so backend clears any prior value.
      if (typeof body.clinic_patient_id === "string" && !body.clinic_patient_id.trim()) {
        body.clinic_patient_id = null;
      }
      // Coerce age → integer or null (backend also validates 0-150).
      if (body.age === "" || body.age === undefined) {
        body.age = null;
      } else {
        const n = parseInt(body.age, 10);
        body.age = Number.isNaN(n) ? null : n;
      }
      const { data } = await api.put(`/patients/${patient.id}`, body);
      onSaved(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === "duplicate_clinic_patient_id") {
        toast.error(`Patient ID already used by ${detail.existing_patient_name || "another patient"}`);
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Save failed"));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-patient-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-ink flex items-center gap-2">
            <Pencil className="w-5 h-5 text-clay" /> Edit patient
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="edit-patient-form">
          <input required data-testid="edit-patient-name" placeholder="Full name" value={f.name}
                 onChange={(e) => setF({ ...f, name: e.target.value })}
                 className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
          <input required data-testid="edit-patient-phone" placeholder="Phone" value={f.phone}
                 onChange={(e) => setF({ ...f, phone: e.target.value })}
                 className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
          <input data-testid="edit-patient-email" type="email" placeholder="Email" value={f.email}
                 onChange={(e) => setF({ ...f, email: e.target.value })}
                 className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
          <div>
            <input
              data-testid="edit-patient-clinic-id"
              placeholder="Patient ID (optional, e.g. PT-2026-0042)"
              value={f.clinic_patient_id}
              onChange={(e) => setF({ ...f, clinic_patient_id: e.target.value })}
              className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm font-mono"
            />
            <div className="text-[10px] text-ink-soft mt-1 px-1">Your clinic's own identifier — unique within the clinic. Leave blank to clear.</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DateDMYInput data-testid="edit-patient-dob" value={f.dob}
                   onChange={(e) => {
                     const dob = e.target.value;
                     const derived = ageFromDob(dob);
                     setF((prev) => ({
                       ...prev,
                       dob,
                       age: derived !== null ? String(derived) : prev.age,
                     }));
                   }}
                   className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
            <input
              data-testid="edit-patient-age"
              type="number"
              min={0}
              max={150}
              step={1}
              inputMode="numeric"
              placeholder="Age (years)"
              value={f.age}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, "");
                if (!raw) return setF((prev) => ({ ...prev, age: "" }));
                const n = Math.min(150, Math.max(0, parseInt(raw, 10)));
                setF((prev) => ({ ...prev, age: String(n) }));
              }}
              className="rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm"
            />
          </div>
          <select data-testid="edit-patient-gender" value={f.gender}
                  onChange={(e) => setF({ ...f, gender: e.target.value })}
                  className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm bg-white">
            <option value="">Gender</option><option>F</option><option>M</option><option>Other</option>
          </select>
          <input data-testid="edit-patient-address" placeholder="Address" value={f.address}
                 onChange={(e) => setF({ ...f, address: e.target.value })}
                 className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
          <input data-testid="edit-patient-allergies" placeholder="Allergies (e.g. Penicillin, Latex)"
                 value={f.allergies}
                 onChange={(e) => setF({ ...f, allergies: e.target.value })}
                 className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
          <textarea data-testid="edit-patient-notes" placeholder="General notes (visible to all clinic staff)"
                    rows={2} value={f.notes}
                    onChange={(e) => setF({ ...f, notes: e.target.value })}
                    className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
          {canWriteDoctorNotes && (
            <div>
              <label className="text-xs uppercase tracking-widest text-ink-mid mb-1 block flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-clay" /> Doctor notes (private — hidden from receptionist)
              </label>
              <textarea data-testid="edit-patient-doctor-notes"
                        placeholder="Private clinical notes only doctors and admins can read"
                        rows={3} value={f.doctor_notes}
                        onChange={(e) => setF({ ...f, doctor_notes: e.target.value })}
                        className="w-full rounded-xl border border-clay/30 bg-clay/5 px-4 py-2.5 text-sm" />
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} data-testid="edit-patient-cancel"
                    className="text-sm px-4 py-2 rounded-full text-ink-mid hover:bg-linen">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="edit-patient-save"
                    className="text-sm px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 inline-flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save changes
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
